'use client';

import { FC, Fragment } from 'react';
import { LandingHero } from './landingHero';
import { LandingHeroImages } from './landingHeroImages';
import { LandingRebates } from './landingRebates';
import { LandingHeroProductShowcase } from './landingHeroProductShowcase';
import { MrSwiper } from './mrSlider/MrSwiper';
import { LandingFeatureShowcase } from './LandingFeatureShowcase';
import { LandingFeatureAccordion } from './landingFeatureAccordion';
import { LandingProductAds } from './landingProductAds';
import { LandingTabInfo } from './landingTabInfo';
import { LandingFeatureAccorNested } from './landingFeatureAccordionNest';
import { MrCards } from './mrCards';
import { MrMarquee } from './mrSlider/MrMarquee';
import { LandingTips } from './landingTips';
import { LandingFaqs } from './landingFaqs';
import { LandingDealers } from './landingDealers';
import { LandingPreviewGrid } from './landingPreviewGrid';
import { ecoRebateData } from 'utils/specificationHelper';
import { useProducts } from '@/stores/product';
import { EnhancedSliderWrapper } from '@/components/product/EnhancedSliderWrapper';
import { ThemedSection, ThemedSectionProps } from './ThemedSection';
import { ThemedHero } from '../Hero/ThemedHero';
import FlatReviews from '../FlatReviews';
import { PaddedSection } from './PaddedSection';
import { InfoCardBanner } from '../InfoCard/InfoCardBanner';
import { StrapiSideBySideData } from './sideBySide';
import PillCallout from '../pillCallout';
import ModelViewer3D from '../3D';
import HlvsImageDescription from '../hlvs-image-description';
import { StyledTextWrapper } from './styledText/StyledTextWrapper';
import { FeatureHero } from '../Hero/FeatureHero';
import { VideoPlayer } from '../video';
import { HVLSCostCutting } from '../HVLS/cost-cutting';
import { IconSliderWrapper } from '../section/iconSlider/IconSliderWrapper';
import FlashCards from '../FlashCards';
import { Hero2025 } from '../Hero/Hero2025';
import ImageBlock from '../ImageBlock';
import { Cards } from '../cards';
import { StaticVideoCards } from '../cards/video';
import { WhatsInTheBox } from './WhatsInTheBox';
import { Footnotes } from '../Footnotes';
import IntertekBlock from '../intertekBlock';
import ChooseYourTier from '../contractor/ChooseYourTier';
import Bazaarvoice from '../bazaarvoice';
import PurchaseSectionDynamic from '../product/PurchaseSectionDynamic';
import TextWithImages from '@/components/TextWithImages';

export interface PropsImage {
  id: string;
  attributes: {
    name: string;
    alternativeText: string | null;
    width: number;
    height: number;
    formats: any;
    url: string;
    mime: string;
    size: any;
  };
}

export interface StickyFooterData {
  content: string;
  buttonText: string;
  buttonLink: string;
  disabled: boolean;
}

export interface MainSectionProps {
  [key: string]: unknown;
  overview_meta_title?: string;
  overview_meta_description?: string;
  pageTheme?: string;
  contents: unknown[];
  createdAt: string;
  related_products: {
    data: unknown[];
  };
  product_variants?: {
    data: any[];
  };
  slug: string;
  title: string;
  updatedAt: string;
  stickyFooter?: StickyFooterData;
}

export const MainSection: FC<{ data: MainSectionProps }> = ({ data }) => {
  const { relatedProducts } = useProducts();
  const rebate = ecoRebateData[data?.slug || ''];

  if (!data) {
    return null;
  }

  const nestedAccordion = data.contents?.filter(
    (item: any) =>
      item?.__component === 'landing.feature-accordion' && item?.nested
  );
  const indexNested = data.contents?.findIndex(
    (item: any) =>
      item?.__component === 'landing.feature-accordion' && item?.nested
  );

  let dataContent =
    nestedAccordion?.length !== 0
      ? data?.contents
          ?.map((item: any, i: number) => {
            if (i === indexNested) {
              return {
                __component: 'landing.feature-accordion-nested',
                data: nestedAccordion,
              };
            }
            return item;
          })
          .filter(
            (item: any) =>
              item?.__component !== 'landing.feature-accordion' ||
              (item?.__component === 'landing.feature-accordion' &&
                !item?.nested)
          )
      : data?.contents;

  if (!rebate)
    dataContent = dataContent?.filter(
      (x: any) => x.__component !== 'landing.rebates'
    );

  return (
    <>
      {dataContent?.map((item: any, index: number) => {
        switch (item.__component) {
          case 'landing.hero':
            if (item?.slider)
              return (
                <LandingHeroImages
                  content={item}
                  slug={data.slug}
                  index={index}
                  key={index}
                />
              );
            return <LandingHero content={item} slug={data.slug} key={index} />;
          case 'landing.enhanced-slider':
            return <EnhancedSliderWrapper strapiData={item} key={index} />;
          case 'landing.icon-slider':
            return <IconSliderWrapper strapiData={item} key={index} />;
          case 'landing.rebates':
            return (
              <LandingRebates content={item} slug={data.slug} key={index} />
            );
          case 'landing.hero-product-showcase':
            if (item?.data?.length !== 0)
              return <LandingPreviewGrid data={item.data} key={index} />;
            return <LandingHeroProductShowcase key={index} content={item} />;
          case 'landing.features-carousel':
            return (
              <Fragment key={index}>
                {item.with_preview ? (
                  <MrCards content={item} />
                ) : (
                  <MrSwiper size='large' data={item.data} content={item} />
                )}
              </Fragment>
            );
          case 'landing.feature-showcase':
            return <LandingFeatureShowcase content={item} key={index} />;
          case 'landing.feature-accordion':
            if (!item?.nested) {
              return <LandingFeatureAccordion key={index} content={item} />;
            }
            return null;
          case 'landing.feature-accordion-nested':
            return (
              <LandingFeatureAccorNested content={item?.data} key={index} />
            );
          case 'landing.product-ads':
            return <LandingProductAds key={index} content={item} />;
          case 'landing.tabs-info':
            return <LandingTabInfo key={index} content={item} />;
          case 'landing.product-parts':
            return <MrMarquee key={index} content={item} />;
          case 'landing.tips':
            return <LandingTips key={index} content={item} />;
          case 'landing.faqs':
            return <LandingFaqs key={index} content={item} />;
          case 'landing.dealers':
            return <LandingDealers key={index} content={item} />;
          case 'landing.related-products':
            return (
              <MrSwiper
                key={`rp-${index}`}
                size='medium'
                arrow='center'
                data={data?.related_products?.data}
                content={item}
              />
            );
          case 'landing.product-variants':
            return (
              <div key={`landing.product-variants`}>
                <MrSwiper
                  key={`pv-${index}`}
                  size='small'
                  data={relatedProducts}
                  content={item}
                  slug={data.slug}
                />
              </div>
            );
          case 'landing.info-banner':
            return (
              <Fragment key={`landing.info-banner-${index}`}>
                <PaddedSection content={item}>
                  <InfoCardBanner props={item}></InfoCardBanner>
                </PaddedSection>
              </Fragment>
            );
          case 'landing.flat-reviews':
            const flatData: {
              padding?: any;
              background?: {
                style?: string;
              };
            } = {
              padding: item?.padding,
              background: item?.sectionBackground,
            };
            return (
              <Fragment key={`landing.flat-reviews-${index}`}>
                <ThemedSection content={flatData}>
                  <FlatReviews data={item}></FlatReviews>
                </ThemedSection>
              </Fragment>
            );
          case 'landing.pill-callout':
            return (
              <Fragment key={`landing.pill-callout-${index}`}>
                <PaddedSection content={item}>
                  <PillCallout data={item}></PillCallout>
                </PaddedSection>
              </Fragment>
            );
          case 'landing.image-block':
            return (
              <Fragment key={`landing.pimage-block-${index}`}>
                <PaddedSection content={item}>
                  <ImageBlock data={item}></ImageBlock>
                </PaddedSection>
              </Fragment>
            );
          case 'landing.intertek-block':
            return (
              <Fragment key={`landing.intertek-block-${index}`}>
                <PaddedSection content={item}>
                  <IntertekBlock data={item}></IntertekBlock>
                </PaddedSection>
              </Fragment>
            );
          case 'landing.card-grid':
            return (
              <Fragment key={`landing.card-grid-${index}`}>
                <PaddedSection content={item}>
                  <FlashCards items={item}></FlashCards>
                </PaddedSection>
              </Fragment>
            );
          case 'landing.hero-banner-w-image-themed':
            return (
              <Fragment key={`landing.hero-banner-w-image-themed-${index}`}>
                <ThemedSection content={item}>
                  <ThemedHero props={item}></ThemedHero>
                </ThemedSection>
              </Fragment>
            );
          case 'landing.feature-hero':
            const itemData: ThemedSectionProps = {
              padding: item?.padding,
              roundedBottom: item?.roundedBottom,
              background: { ...item?.sectionBackground },
            };

            return (
              <Fragment key={`landing.feature-hero-${index}`}>
                <ThemedSection content={itemData}>
                  <FeatureHero props={item}></FeatureHero>
                </ThemedSection>
              </Fragment>
            );
          case 'heros.hero2025':
            const hero2025Data: ThemedSectionProps = {
              padding: {
                top: 1,
              },
              roundedBottom: item?.roundedBottom,
              background: { ...item?.sectionBackground },
            };
            return (
              <Fragment key={`landing.feature-hero-${index}`}>
                <ThemedSection content={hero2025Data}>
                  <Hero2025 props={item}></Hero2025>
                </ThemedSection>
              </Fragment>
            );
          case 'landing.side-by-side':
            return (
              <Fragment key={`landing.side-by-side-${index}`}>
                <StrapiSideBySideData content={item} />
              </Fragment>
            );
          case 'landing.3-d-viewer':
            return (
              <Fragment key={`landing.3-d-viewer-${index}`}>
                <ModelViewer3D {...item} />
              </Fragment>
            );
          case 'landing.image-with-description':
            return (
              <Fragment key={`landing.image-with-description-${index}`}>
                <HlvsImageDescription {...item} />
              </Fragment>
            );
          case 'landing.styled-text':
            return (
              <Fragment key={`landing.styled-text-${index}`}>
                <StyledTextWrapper strapiData={item} />
              </Fragment>
            );
          case 'landing.mr-video':
            return (
              <Fragment key={`landing.mr-video-${index}`}>
                <PaddedSection content={item}>
                  <VideoPlayer props={item} />
                </PaddedSection>
              </Fragment>
            );
          case 'hvls.cost-cutting':
            return (
              <Fragment key={`hvls.cost-cutting-${index}`}>
                <PaddedSection content={item}>
                  <HVLSCostCutting props={item} />
                </PaddedSection>
              </Fragment>
            );
          case 'landing.cards':
            return (
              <Fragment key={`landing.cards-${index}`}>
                <PaddedSection content={item}>
                  <Cards props={item} />
                </PaddedSection>
              </Fragment>
            );
          case 'landing.video-cards':
            return (
              <Fragment key={`landing.video-cards-${index}`}>
                <PaddedSection content={item}>
                  <StaticVideoCards props={item} />
                </PaddedSection>
              </Fragment>
            );
          case 'landing.what-s-in-the-box':
            return <WhatsInTheBox key={index} content={item} />;
          case 'landing.contractor-reward-tiers':
            return <ChooseYourTier key={index} content={item} />;
          case 'pages.footnotes':
            if (item.disabled) return null;

            return (
              <Fragment key={`landing.footnotes-${index}`}>
                <PaddedSection content={item}>
                  <Footnotes props={item} />
                </PaddedSection>
              </Fragment>
            );
          case 'landing.bazaarvoice':
            return <Bazaarvoice key={index} content={item} />;
          case 'landing.purchase-section-dynamic':
            return <PurchaseSectionDynamic key={index} content={item} />;
          case 'landing.text-with-images':
            return (
              <Fragment key={`landing.text-with-images-${index}`}>
                <TextWithImages content={item} />
              </Fragment>
            );
          default:
            return <Fragment key={index}></Fragment>;
        }
      })}
    </>
  );
};
